// Main-thread proxy for the long-lived parse Worker. Owns the Worker handle
// and exposes a single `requestParse(source, caretOffset, scopeStrategy)`
// entry returning a Promise that resolves with the serialized parse body.
//
// Per `Slice: Parse-Time Mitigation`: requests are dispatched against a
// shared module-scope Worker so the renderer pays the bundle-load cost
// exactly once per session. Concurrent requests are correlated via
// `requestId`; only the latest request's reply is observed by the caller —
// stale replies are dropped.

import type {
  InitWorkerRequest,
  ParseScopeStrategy,
  ParseWorkerMessage,
  ParseWorkerRequest,
  ScopeWorkerReply,
  ScopeWorkerRequest,
  SyntaxWorkerReply,
  SyntaxWorkerRequest,
  WorkerInboundMessage,
} from './parseWorker';
import type { SerializedParsedTSqlBody } from './model';

export type { ParseScopeStrategy } from './parseWorker';
export type {
  ScopeWorkerReply,
  SyntaxWorkerReply,
  WorkerRoutineParam,
  WorkerScopeLocal,
} from './parseWorker';

export interface RequestParseArgs {
  readonly source: string;
  readonly caretOffset: number;
  readonly scopeStrategy: ParseScopeStrategy;
}

export interface RequestParseResult {
  readonly body: SerializedParsedTSqlBody;
  readonly scopedFastPath: boolean;
  readonly execLiteralShortCircuit: boolean;
  readonly workerMs: number;
}

/** Shape of a worker `error` event as consumed here — structural subset of
 *  the DOM `ErrorEvent` so stubs don't need `lib.dom.d.ts`. */
export interface WorkerErrorEventLike {
  readonly message?: string;
  readonly filename?: string;
  readonly lineno?: number;
}

/** Subset of the DOM `Worker` surface used by the proxy — narrow so tests
 *  can substitute a stub without depending on `lib.dom.d.ts`. */
export interface ParseWorkerHandle {
  postMessage(msg: WorkerInboundMessage): void;
  addEventListener(
    event: 'message',
    listener: (event: MessageEvent<ParseWorkerMessage>) => void,
  ): void;
  addEventListener(
    event: 'error',
    listener: (event: WorkerErrorEventLike) => void,
  ): void;
  addEventListener(event: 'messageerror', listener: (event: unknown) => void): void;
  removeEventListener(
    event: 'message',
    listener: (event: MessageEvent<ParseWorkerMessage>) => void,
  ): void;
  terminate(): void;
}

let cachedWorker: ParseWorkerHandle | null = null;

/** Monotonic count of parse-Worker spawns this session — bumped each time
 *  `ensureWorker` constructs a NEW Worker (initial spawn + every lazy respawn
 *  after a `respawnParseWorker`/`disposeParseWorker` teardown). Callers that
 *  cache "did I already do X for the current worker instance" (e.g. the F3
 *  procedural cold-start warm-up latch) key on this so their per-worker state
 *  resets exactly when the worker restarts. Never resets — a fresh worker is
 *  always a strictly higher generation. */
let workerGeneration = 0;

/** Reject callbacks for every in-flight request (parse + scope + syntax),
 *  keyed by requestId. A worker whose SCRIPT fails to load never posts a reply — it
 *  fires an `error` event instead. Without this registry that class of
 *  failure is silent: every request would misclassify as a deadline (stall,
 *  no latch, no telemetry). The `error`/`messageerror` listeners installed in
 *  `ensureWorker` reject everything here so the caller's failure ladder
 *  (latch-to-inline) engages. */
const pendingRejects = new Map<string, (err: Error) => void>();

function failAllPending(err: Error): void {
  const rejects = [...pendingRejects.values()];
  pendingRejects.clear();
  for (const reject of rejects) reject(err);
}

/** Pluggable factory — the production path (renderer) constructs a real
 *  `Worker` from the bundled module URL; tests substitute a stub. */
export type ParseWorkerFactory = () => ParseWorkerHandle;
let workerFactory: ParseWorkerFactory | null = null;

export function __setParseWorkerFactoryForTest(factory: ParseWorkerFactory | null): void {
  if (cachedWorker) {
    cachedWorker.terminate();
    cachedWorker = null;
  }
  pendingRejects.clear();
  workerFactory = factory;
}

function defaultFactory(): ParseWorkerHandle {
  // Renderer-side default: spawn the worker from `parseWorker.ts`. Vite's
  // worker plugin recognises the `new Worker(new URL(..., import.meta.url),
  // { type: 'module' })` pattern and emits a separate worker bundle.
  const worker = new Worker(new URL('./parseWorker.ts', import.meta.url), {
    type: 'module',
  });
  return worker as unknown as ParseWorkerHandle;
}

/** Compute the ANTLR bundle URL in the client's MAIN-CHUNK `import.meta.url`
 *  context — the SAME expression as `load.ts:71`. Sent to the worker via the
 *  `init` message (D2) so the worker never resolves its own worker-chunk URL
 *  (Trap 3: a Vite-emitted worker asset's `import.meta.url` points at a
 *  different build location and the relative walk isn't guaranteed to resolve). */
function computeBundleUrl(): string {
  return new URL('../../../resources/antlr/tsql.js', import.meta.url).href;
}

function ensureWorker(): ParseWorkerHandle {
  if (cachedWorker) return cachedWorker;
  const worker = (workerFactory ?? defaultFactory)();
  cachedWorker = worker;
  // A new Worker instance = a new generation (bumped only AFTER a successful
  // construction so a factory throw doesn't advance the counter).
  workerGeneration += 1;
  // Script-load / uncaught-worker errors surface as `error` events, NOT
  // message replies — reject every in-flight request so callers' failure
  // ladders engage instead of stalling to their deadlines (plan16 T2 review
  // I1). `messageerror` (a reply that failed structured clone) likewise.
  worker.addEventListener('error', (event: WorkerErrorEventLike) => {
    const where = event.filename ? ` (${event.filename}:${event.lineno ?? 0})` : '';
    failAllPending(
      new Error(`parse worker script error: ${event.message ?? 'unknown'}${where}`),
    );
  });
  worker.addEventListener('messageerror', () => {
    failAllPending(new Error('parse worker messageerror: reply failed structured clone'));
  });
  // D2: bootstrap the worker with the bundle URL immediately after spawn, so
  // it is available before the first parse/scope request resolves the bundle.
  const init: InitWorkerRequest = { type: 'init', bundleUrl: computeBundleUrl() };
  worker.postMessage(init);
  return cachedWorker;
}

let nextRequestId = 1;
let nextScopeRequestId = 1;

export function requestParse(args: RequestParseArgs): Promise<RequestParseResult> {
  const worker = ensureWorker();
  const requestId = `tsql-parse-${nextRequestId++}`;
  return new Promise<RequestParseResult>((resolve, reject) => {
    const onMessage = (
      event: MessageEvent<ParseWorkerMessage>,
    ): void => {
      const data = event.data;
      if (!data || data.requestId !== requestId) return;
      if (data.type === 'error') {
        worker.removeEventListener('message', onMessage);
        pendingRejects.delete(requestId);
        reject(new Error(data.error));
        return;
      }
      // Only this request's parse reply resolves it; a stray scope reply
      // sharing the (distinct-prefix) id space is ignored defensively.
      if (data.type !== 'parsed') return;
      worker.removeEventListener('message', onMessage);
      pendingRejects.delete(requestId);
      resolve({
        body: data.body,
        scopedFastPath: data.scopedFastPath,
        execLiteralShortCircuit: data.execLiteralShortCircuit,
        workerMs: data.workerMs,
      });
    };
    worker.addEventListener('message', onMessage);
    pendingRejects.set(requestId, (err) => {
      worker.removeEventListener('message', onMessage);
      reject(err);
    });
    const request: ParseWorkerRequest = {
      type: 'parse',
      requestId,
      source: args.source,
      caretOffset: args.caretOffset,
      scopeStrategy: args.scopeStrategy,
    };
    worker.postMessage(request);
  });
}

/** Request worker-side procedural-scope extraction for the caret. Resolves
 *  with the plain scope reply (locals + routine params + fast-path flags);
 *  mirrors `requestParse`'s correlation + one-shot listener cleanup. */
export function requestProceduralScope(args: {
  source: string;
  caretOffset: number;
}): Promise<ScopeWorkerReply> {
  const worker = ensureWorker();
  const requestId = `tsql-scope-${nextScopeRequestId++}`;
  return new Promise<ScopeWorkerReply>((resolve, reject) => {
    const onMessage = (
      event: MessageEvent<ParseWorkerMessage>,
    ): void => {
      const data = event.data;
      if (!data || data.requestId !== requestId) return;
      if (data.type === 'error') {
        worker.removeEventListener('message', onMessage);
        pendingRejects.delete(requestId);
        reject(new Error(data.error));
        return;
      }
      if (data.type !== 'scope') return;
      worker.removeEventListener('message', onMessage);
      pendingRejects.delete(requestId);
      resolve(data);
    };
    worker.addEventListener('message', onMessage);
    pendingRejects.set(requestId, (err) => {
      worker.removeEventListener('message', onMessage);
      reject(err);
    });
    const request: ScopeWorkerRequest = {
      type: 'scope',
      requestId,
      source: args.source,
      caretOffset: args.caretOffset,
    };
    worker.postMessage(request);
  });
}

let nextSyntaxRequestId = 1;

/** Request the worker-carried raw ANTLR parse errors for `source` (plan20 T2
 *  wedge fix). Deliberately NO deadline here — syntax markers are eventually-
 *  consistent; the caller (`workerSyntaxErrorSource.ts`) owns trailing
 *  coalescing + the hard-cap respawn, and SqlMonaco owns model-version
 *  stamping so a late reply for edited text is dropped, never painted. */
export function requestSyntaxErrors(args: { source: string }): Promise<SyntaxWorkerReply> {
  const worker = ensureWorker();
  const requestId = `tsql-syntax-${nextSyntaxRequestId++}`;
  return new Promise<SyntaxWorkerReply>((resolve, reject) => {
    const onMessage = (event: MessageEvent<ParseWorkerMessage>): void => {
      const data = event.data;
      if (!data || data.requestId !== requestId) return;
      if (data.type === 'error') {
        worker.removeEventListener('message', onMessage);
        pendingRejects.delete(requestId);
        reject(new Error(data.error));
        return;
      }
      if (data.type !== 'syntax') return;
      worker.removeEventListener('message', onMessage);
      pendingRejects.delete(requestId);
      resolve(data);
    };
    worker.addEventListener('message', onMessage);
    pendingRejects.set(requestId, (err) => {
      worker.removeEventListener('message', onMessage);
      reject(err);
    });
    const request: SyntaxWorkerRequest = {
      type: 'syntax',
      requestId,
      source: args.source,
    };
    worker.postMessage(request);
  });
}

/** F2 (plan20 T2 fold-in): hard-reset a wedged worker. Rejects every pending
 *  request (each reject self-removes its message listener), terminates the
 *  Worker, and clears the cache so the NEXT request lazily spawns a fresh one.
 *  Callers invoke this only on hard-cap expiry — a job pinned that long means
 *  the single worker thread is almost certainly hung, and without a terminate
 *  it would stay wedged for the rest of the session. */
export function respawnParseWorker(reason: string): void {
  const err = new Error(`parse worker respawned: ${reason}`);
  (err as Error & { jamSqlWorkerRespawn?: true }).jamSqlWorkerRespawn = true;
  failAllPending(err);
  if (cachedWorker) {
    cachedWorker.terminate();
    cachedWorker = null;
  }
}

/** Ensure the parse Worker is spawned (lazily, exactly like a parse/scope/
 *  syntax request would) and return its current generation. Used by the F3
 *  procedural cold-start warm-up: it spawns the worker so the ANTLR bundle load
 *  starts EARLY (before the first completion races the ~17 s cold load), and
 *  returns the generation so the caller's once-per-document warm-up latch keys
 *  on the live worker instance. May throw if worker construction fails — the
 *  warm-up caller swallows it (fire-and-forget). */
export function ensureParseWorkerGeneration(): number {
  ensureWorker();
  return workerGeneration;
}

/** True iff `err` is the rejection produced by `respawnParseWorker` — a
 *  deliberate hard-reset, NOT evidence the worker/bundle is unavailable.
 *  Callers with a session latch (inline-only, syntax-off) must NOT trip it on
 *  this class: the freshly-respawned worker may be perfectly healthy. */
export function isWorkerRespawnError(err: unknown): boolean {
  return (
    err instanceof Error &&
    (err as Error & { jamSqlWorkerRespawn?: true }).jamSqlWorkerRespawn === true
  );
}

/** Tear down the Worker (e.g. on signing out / disposing the editor). The
 *  next `requestParse` call lazily spawns a fresh Worker. */
export function disposeParseWorker(): void {
  if (cachedWorker) {
    cachedWorker.terminate();
    cachedWorker = null;
  }
  pendingRejects.clear();
}
